import { useMemo, useState } from "react";

import Container from "../../Container";
import { FilterFood } from "../../../utils/filters";
import Filters from "../../Filters";
import Search from "../../Search";
import SectionStrip from "../Browse";
import { Title } from "..";
import { useStateValue } from "../../../context/StateProvider";
import { searchProducts } from "../../../utils/search";
import {
  sectionProducts,
  sectionTypes,
  stockedSections,
} from "../../../utils/sections";

/*
 * Menu browsing, three levels deep:
 *
 *   section  ->  category  ->  sub-category
 *
 * Each level narrows the grid below it. Picking a section rewrites the category
 * bar to only that section's categories; picking a category with children opens
 * its sub-categories. Any level can be left at "all".
 *
 * Search sits outside that hierarchy: someone typing "burger" wants burgers
 * wherever they live, so a query searches the whole catalogue and the browse
 * controls step aside until it is cleared.
 */
const Menu = ({ title }: { title?: string }) => {
  const [{ foodItems, categories, sections }] = useStateValue();
  const [filter, setFilter] = useState<string>("all");
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [query, setQuery] = useState<string>("");

  const searching = query.trim().length > 0;

  const visibleSections = useMemo(
    () => stockedSections(sections || [], foodItems, categories),
    [sections, foodItems, categories]
  );

  const activeSection = useMemo(
    () => visibleSections.find((s) => s.id === sectionId) || null,
    [visibleSections, sectionId]
  );

  // Limits the category bar to the current section. null means no restriction.
  const restrictTo = useMemo(
    () => (activeSection ? sectionTypes(categories, activeSection) : null),
    [categories, activeSection]
  );

  const items = useMemo(() => {
    if (searching) return searchProducts(foodItems, categories, query);
    if (filter !== "all") return FilterFood(foodItems, categories, filter);
    if (activeSection) return sectionProducts(foodItems, categories, activeSection);
    return foodItems;
  }, [searching, query, filter, foodItems, categories, activeSection]);

  // A category chosen inside one section is meaningless in the next, so the
  // narrower level resets whenever the wider one changes.
  const chooseSection = (id: string | null) => {
    setSectionId(id);
    setFilter("all");
  };

  // Starting a search drops the browse position, so clearing it returns to the
  // full menu rather than to a scope the results were never limited by.
  const onSearch = (v: string) => {
    setQuery(v);
    if (v.trim()) {
      setSectionId(null);
      setFilter("all");
    }
  };

  return (
    <section className="w-full my-5" id="menu">
      <div className="w-full flex items-center justify-center">
        <Title
          title={
            searching
              ? "Search results"
              : activeSection?.header || title || "Our Hot Products"
          }
          center
        />
      </div>

      <div className="w-full mt-6">
        <Search value={query} onChange={onSearch} resultCount={items?.length} />
      </div>

      {!searching && (
        <>
          <div className="w-full mt-6">
            <SectionStrip
              sections={visibleSections}
              activeId={sectionId}
              onSelect={chooseSection}
            />
          </div>

          <Filters filter={filter} setFilter={setFilter} restrictTo={restrictTo} />
        </>
      )}

      <Container
        className="bg-containerbg"
        col
        scrollOffset={0}
        items={items}
      />
    </section>
  );
};

export default Menu;
